import {
    BetInfo,
    RoundInfo,
    LanguageService,
    CoreDI,
    IEventManagerWithAsync,
    inject,
    injectable,
    ModuleIsEnabledFilter, DI, ILogic
} from '@gongxigames/framework/slot';
import {consoleLogger} from '@gongxigames/framework/core/fragments/ConsoleLogger';
import type {RubyPlayGameClientWrapperSettings} from '@gongxigames/framework/integrations/rubyplay/RubyPlayGameClientWrapperSettings';
import {
    OptionsNames,
    Game2GCMClientSideMessagesMap,
    PaidUpdateResponse,
    BalancesHasChangedResponse,
    BalancesUpdateResponse,
    OptionHasChangedResponse,
    HandleFreeRoundsUpdateResponse,
    Options,
    OGSParamsResponse,
    ResumeResponse
} from '@gongxigames/framework/integrations/lightAndWonder/LightAndWonderType';
import {
    BottomPanelEvents,
    KeypadControlsEvents,
    LogicEvents,
    InfoPageEvents,
    GameRulesEvents,
    GameConfig
} from '@gongxigames/framework/slot';
import {ILightAndWonderMessageChannel} from '@gongxigames/framework/integrations/lightAndWonder/LightAndWonderInterface';
import {LightAndWonderIntegrationDI} from '@gongxigames/framework/integrations/lightAndWonder/LightAndWonderIntegrationDI';
import {isEnabledLightAndWonder} from '@gongxigames/framework/integrations/lightAndWonder/isEnabledLightAndWonder';
import {strictUiEmitter} from '@gongxigames/framework/slotV2/globals';

@injectable()
export class LightAndWonderAPIAdapter {
    public static readonly NAME = LightAndWonderIntegrationDI.APIAdapter;
    public static readonly isEnabled: ModuleIsEnabledFilter = isEnabledLightAndWonder;

    private balance?: number;
    private bonusBalance?: number;
    private realBalance?: number;
    private optionChangeMap: Record<Options, (value: boolean) => void> = {
        PAYTABLE: (value) => strictUiEmitter.emit(value ? 'UI.menu.infoPageClicked': 'UI.menu.closed'),
        ABOUT: (value) => strictUiEmitter.emit(value ? 'UI.menu.gameRulesClicked' : 'UI.menu.closed'),
        MUTE: (value) => value ? strictUiEmitter.emit('UI.sounds.disable') : strictUiEmitter.emit('UI.sounds.enable'),
        TURBO: (value) => this.eventManager.emit(KeypadControlsEvents.TOGGLE_TURBO_BUTTON, value)
    };
    private isTurboEnabled: boolean;

    constructor(
        @inject(CoreDI.IEventManagerWithAsync) private eventManager: IEventManagerWithAsync,
        @inject(LightAndWonderIntegrationDI.MessageChannel) private messageChannel: ILightAndWonderMessageChannel,
        @inject(CoreDI.LanguageService) protected languageService: LanguageService,
        @inject(CoreDI.GameConfig) protected gameConfig: GameConfig,
        @inject(CoreDI.Settings) protected _settings: RubyPlayGameClientWrapperSettings,
        @inject(DI.Logic) private logic: ILogic
    ) {
        this.registerOptions();
        this.setGameConfig();
        this.setupOnEvents();
        this.setupSendEvents();
        this.setupInitialSoundConfig();
        this.isTurboEnabled = this._settings.get('quickSpin');
    }

    private refreshBalanceRequest(): void {
        this.eventManager.emit('lnw.refreshBalanceRequest');
    }

    private getGameName(): string {
        const localizeGameName = this.languageService.get('gameName');
        const str = localizeGameName === 'undefined' ? this.gameConfig.gameName : localizeGameName;

        return `${str} - RubyPlay Games`;
    }

    private registerOptions(): void {
        for (const item of OptionsNames) {
            this.sendMsgToGCM('com.lnw.gcm.regOption', {
                optionType: `${item}`
            });
        }
    }

    private setupInitialSoundConfig(): void {
        const {soundsMuted, soundEnabled} = this._settings.getAllProperties();
        const sounds = soundEnabled && !soundsMuted;

        this.sendSoundStateUpdate(sounds);
    }

    private setGameConfig(): void {
        this.sendMsgToGCM('com.lnw.gcm.setGameConfig', {
            cuiPosition: 'right',
            gameLoadingScreen: true,
            gameName: this.getGameName()
        });
    }

    private setupOnEvents(): void {
        this.on('com.lnw.gcm.resume', this.onResume);
        this.on('com.lnw.gcm.setOGSParams', this.setOGSParams);
        this.on('com.lnw.gcm.paidUpdateResponse', this.onPaidUpdateResponse);
        this.on('com.lnw.gcm.balancesHasChanged', this.onBalancesHasChangedResponse);
        this.on('com.lnw.gcm.balancesUpdateResponse', this.onBalancesUpdateResponse);
        this.on('com.lnw.gcm.optionHasChanged', this.onOptionHasChangedResponse);
        this.on('com.lnw.gcm.stopAutoPlay', this.onStopAutoPlay);
        this.on('com.lnw.gcm.handleFreeRoundsUpdate', this.onHandleFreeRoundsUpdateResponse);
        this.on('com.lnw.gcm.gameRevealed', this.onGameRevealed);
    }

    private setupSendEvents(): void {
        this.on('General.Game.INITIALIZED', this.gameReady);

        this.on('General.UI.BALANCE.UPDATED', this.onBalanceUpdate);
        this.on(LogicEvents.BALANCE_UPDATED, this.onBalanceUpdate);

        this.on(LogicEvents.ROUND_START, this.onPaidRoundStarted);
        this.on('General.Flow.PAID_ROUND_ENDED', this.onPaidRoundEnded);

        this.on(LogicEvents.BET_CHANGED, this.onUpdateBet);

        strictUiEmitter.on('UI.sounds.switchedTo', this.sendSoundStateUpdate.bind(this));
        this.on(KeypadControlsEvents.TURBO_BUTTON_CLICKED, this.onTurboBtnClicked);
        this.on('UI.Buttons.TurboMode', this.onTurboBtnClicked);

        this.on(KeypadControlsEvents.INFO_CLICKED, this.sendPayTableStateUpdate.bind(this, true));
        strictUiEmitter.on('UI.menu.infoPageClicked', this.sendPayTableStateUpdate.bind(this, true));
        this.on(InfoPageEvents.CLOSED, this.sendPayTableStateUpdate.bind(this, false));

        strictUiEmitter.on('UI.menu.gameRulesClicked', this.sendGameRulesStateUpdate.bind(this, true));
        this.on(BottomPanelEvents.GAME_RULES_CLICKED, this.sendGameRulesStateUpdate.bind(this, true));
        this.on(GameRulesEvents.CLOSED, this.sendGameRulesStateUpdate.bind(this, false));
    }

    private sendMsgToGCM<K extends keyof Game2GCMClientSideMessagesMap>(eventName: K, eventData?: Game2GCMClientSideMessagesMap[K]): void {
        this.messageChannel.sendMsgToGCM(eventName, eventData);
    }

    private on(id: string | symbol, listener: Function, priority?: number): void {
        this.eventManager.on(id, listener.bind(this, id), this, priority);
    }

    private onResume(id: string | symbol, resumeResponse: ResumeResponse): void {
        this.eventManager.emit('General.UI.ENABLE', LightAndWonderAPIAdapter.NAME);
    }

    private setOGSParams(id: string | symbol, ogsParams: OGSParamsResponse): void {
        consoleLogger.log('setOGSParams: ', ogsParams);
        this.sendMsgToGCM('com.lnw.gcm.loadProgressUpdate', {percent: 0});
    }

    private onPaidUpdateResponse(id: string | symbol, data: PaidUpdateResponse<{value: number;}>): void {
        if (data?.formattedValue?.value > 0) {
            this.refreshBalanceRequest();
        }
    }

    private onBalancesHasChangedResponse(id: string | symbol, data: BalancesHasChangedResponse): void {
        this.refreshBalanceRequest();
    }

    private onBalancesUpdateResponse(id: string | symbol, data: BalancesUpdateResponse): void {
    }

    private onOptionHasChangedResponse(id: string | symbol, {option, value}: OptionHasChangedResponse): void {
        this.optionChangeMap[option](value);
    }

    private onStopAutoPlay(id: string | symbol): void {
        this.eventManager.emit('Autoplay.FORCE_STOP');
    }

    private onHandleFreeRoundsUpdateResponse(id: string | symbol, data: HandleFreeRoundsUpdateResponse): void {
    }

    private onGameRevealed(id: string | symbol): void {
        this.eventManager.emit('General.Game.ENABLE_FIRST_INTERACTION', true);
    }

    private gameReady(): void {
        const totalBet = this.logic.getTotalBet();

        this.sendMsgToGCM('com.lnw.gcm.stakeUpdate', {
            amount: this.getMoneyWithSubunit(totalBet)
        });
        this.sendMsgToGCM('com.lnw.gcm.loadProgressUpdate', {percent: 100});
        this.messageChannel.gameReadyMessage(false);
        this.sendMsgToGCM('com.lnw.gcm.paidUpdate', {
            amount: 0
        });
    }

    private onBalanceUpdate(id: string | symbol, balance: number): void {
        if (this.balance !== balance) {
            this.balance = balance;
            const BONUS = this.bonusBalance ?? 0;
            const CASH = this.getMoneyWithSubunit(this.balance) - BONUS;

            this.sendMsgToGCM('com.lnw.gcm.balancesUpdate', {
                balances: {CASH, BONUS}
            });
        }
    }

    private getMoneyWithSubunit(value: number): number {
        return value / this._settings.get('subunit');
    }

    private onPaidRoundStarted(id: string | symbol, bet: number): void {
        if (bet) {
            this.sendMsgToGCM('com.lnw.gcm.gameAnimationStart');
            this.sendMsgToGCM('com.lnw.gcm.paidUpdate', {
                amount: 0
            });
        }
    }

    private onPaidRoundEnded(id: string | symbol, data: RoundInfo): void {
        this.sendMsgToGCM('com.lnw.gcm.paidUpdate', {
            amount: this.getMoneyWithSubunit(data.totalWin)
        });
        this.sendMsgToGCM('com.lnw.gcm.gameAnimationComplete');
    }

    private onUpdateBet(id: string | symbol, {totalBet}: BetInfo): void {
        this.sendMsgToGCM('com.lnw.gcm.stakeUpdate', {
            amount: this.getMoneyWithSubunit(totalBet)
        });
    }

    private sendSoundStateUpdate(enabled: boolean): void {
        this.sendMsgToGCM('com.lnw.gcm.optionHasChanged', {
            optionType: 'MUTE',
            changedFrom: 'GAME',
            newValue: !enabled
        });
    }

    private onTurboBtnClicked(id: string | symbol, enabled: boolean): void {
        if (this.isTurboEnabled !== enabled) {
            this.isTurboEnabled = enabled;
            this.sendMsgToGCM('com.lnw.gcm.optionHasChanged', {
                optionType: 'TURBO',
                changedFrom: 'GAME',
                newValue: this.isTurboEnabled
            });
        }
    }

    private sendPayTableStateUpdate(isOpen: boolean): void {
        this.sendMsgToGCM('com.lnw.gcm.optionHasChanged', {
            optionType: 'PAYTABLE',
            changedFrom: 'GAME',
            newValue: isOpen
        });
    }

    private sendGameRulesStateUpdate(isOpen: boolean): void {
        this.sendMsgToGCM('com.lnw.gcm.optionHasChanged', {
            optionType: 'ABOUT',
            changedFrom: 'GAME',
            newValue: isOpen
        });
    }

    public updateBonusBalanceData({realBalance, bonusBalance}: {bonusBalance: number; realBalance: number;}): void {
        this.bonusBalance = bonusBalance;
        this.realBalance = realBalance;
    }
}
